import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  VStack,
  useToast,
  HStack,
  Text,
  IconButton,
  Flex,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
  Skeleton,
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  Badge,
  SimpleGrid,
  Input,
  InputGroup,
  InputLeftElement,
  Select,
} from "@chakra-ui/react";
import { useDispatch, useSelector } from "react-redux";
import {
  getData,
  postData,
  deleteData,
  getDataSuperAdmin,
  postDataSuperAdmin,
} from "../../../redux/actions/common.action";
import { useNavigate } from "react-router-dom";
import { DeleteIcon, EditIcon } from "@chakra-ui/icons";
import {
  ArrowLeft,
  ChevronRight,
  Home,
  Plus,
  Search,
  Filter,
  RefreshCcw,
  Mail,
} from "lucide-react";
import { Select as ChakraSelect } from "chakra-react-select";
import DeleteConfirmationModal from "../../../components/DeleteConfirmationModal";
import PopupModelAdd from "../../../components/PopupModelAdd";
import AdminSelect from "../../../helpers/AdminSelect";
import { MdSecurity } from "react-icons/md";

interface TermsItem {
  _id: string;
  title: string;
  terms: string;
  category: string;
  parentId: string;
}

interface TermsState {
  proposalTerms: TermsItem[];
  superTermsCategories: {
    _id: string;
    name: string;
  }[];
  loading: boolean;
  signinuser: {
    userId: string;
    parentId: string;
    UserType: number;
    UserProfile: string;
  };
  termsCategories: {
    _id: string;
    name: string;
  }[];
  superProposalTerms: TermsItem[];
}

const ProposalTerms = () => {
  const [selectedAdmin, setSelectedAdmin] = useState<any>("");
  const {
    proposalTerms,
    loading,
    signinuser,
    termsCategories,
    superTermsCategories,
    superProposalTerms,
  } = useSelector((state: { common: TermsState }) => state.common);

  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [newCat, setNewCat] = useState<string>("");
  const [isCategoryOpen, setIscategoryOpen] = useState<boolean>(false);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [deleteTitle, setDeleteTitle] = useState<string>("");
  const [showFilters, setShowFilters] = useState(false);

  const toast = useToast();
  const dispatch: any = useDispatch();
  const navigate = useNavigate();

  const filteredProposalTerms =
    signinuser.UserType === 1 && !selectedAdmin
      ? superProposalTerms
      : signinuser.UserType === 1 && selectedAdmin
      ? proposalTerms
      : signinuser.UserType !== 1
      ? proposalTerms
      : null;

  const filteredTermsCategories =
    signinuser.UserType === 1 && !selectedAdmin
      ? superTermsCategories
      : signinuser.UserType === 1 && selectedAdmin
      ? termsCategories
      : signinuser.UserType !== 1
      ? termsCategories
      : null;

  useEffect(() => {
    if (signinuser.UserType === 1) {
      dispatch(getDataSuperAdmin("superTermsCategories"));
      dispatch(getDataSuperAdmin("superProposalTerms"));
    } else if (signinuser.UserType !== 1) {
      dispatch(getData("proposalTerms"));
      dispatch(getData("termsCategory"));
    }
  }, [dispatch]);

  useEffect(() => {
    if (!selectedAdmin) return;
    dispatch(getDataSuperAdmin(`proposalTerms?parentId=${selectedAdmin}`));
    dispatch(getDataSuperAdmin(`termsCategory?parentId=${selectedAdmin}`));
  }, [selectedAdmin]);

  const handleBack = () => {
    navigate("/proposal-list");
  };

  const handleAddCategory = () => {
    if (!newCat.trim()) {
      return toast({
        title: "Please add category name.",
        status: "error",
        position: "top-right",
        isClosable: true,
      });
    }
    const payload = {
      name: newCat,
      createdBy: signinuser?.userId,
    };

    if (signinuser?.UserType !== 1) {
      dispatch(postData(payload, "termsCategory", navigate, toast)).then(() => {
        setNewCat("");
        dispatch(getData("termsCategory"));
      });
    } else {
      dispatch(
        postDataSuperAdmin(payload, "termsCategory", navigate, toast)
      ).then(() => {
        setNewCat("");
        dispatch(getDataSuperAdmin("superTermsCategories"));
      });
    }

    setIscategoryOpen(false);
  };

  const handleDelete = (id: string, title: string) => {
    setDeleteId(id);
    setDeleteTitle(title);
    setIsDeleteOpen(true);
  };

  const confirmDelete = () => {
    if (deleteId) {
      dispatch(deleteData(deleteId, "proposalTerms", toast)).then(() => {
        dispatch(getData("proposalTerms"));
      });
      setIsDeleteOpen(false);
      setDeleteId(null);
      setDeleteTitle("");
    }
  };

  const handleClearFilters = () => {
    setSearchTerm("");
    setSelectedCategory(null);
    setShowFilters(false);

    dispatch(getData("proposalTerms"));
    dispatch(getData("termsCategory"));
  };

  const getActiveFiltersCount = () => {
    let count = 0;
    if (searchTerm) count++;
    if (selectedCategory) count++;
    return count;
  };

  const filteredTerms = filteredProposalTerms
    ?.filter((item: TermsItem) =>
      selectedCategory ? item.category === selectedCategory : true
    )
    .filter((item: TermsItem) =>
      searchTerm
        ? item.title.toLowerCase().includes(searchTerm.toLowerCase())
        : true
    );

  return (
    <>
      <DeleteConfirmationModal
        isOpen={isDeleteOpen}
        onClose={() => setIsDeleteOpen(false)}
        onConfirm={confirmDelete}
        title="Delete Terms"
        itemName={deleteTitle}
        itemType="terms"
        warningMessage="This terms template will be permanently deleted. This action cannot be undone."
      />

      <PopupModelAdd
        isOpen={isCategoryOpen}
        onClose={() => setIscategoryOpen(false)}
        value={newCat}
        setValue={setNewCat}
        handleAdd={handleAddCategory}
        title="Create Category"
        placeholder="Enter category name"
        buttonText="Create"
      />

      {signinuser.UserType === 1 && signinuser?.UserProfile === "super" ? (
        <Box px={{ base: 4, md: 10 }} py={3}>
          <AdminSelect
            setSelectedAdmin={setSelectedAdmin}
            selectedAdmin={selectedAdmin}
            endpoint={"proposal"}
          />
        </Box>
      ) : null}

      {/* Modern Header */}
      <Box
        bg="white"
        borderRadius="xl"
        boxShadow="sm"
        border="1px"
        borderColor="gray.200"
        mb="8px"
        p={4}
        mx={{ base: 2, md: 4 }}
        mt={4}
      >
        <Flex justify="space-between" align="center" flexWrap="wrap" gap={4}>
          <VStack align="start" spacing={2}>
            <Button
              leftIcon={<ArrowLeft size={16} />}
              onClick={handleBack}
              size="sm"
              variant="ghost"
              colorScheme="blue"
              fontWeight="medium"
              fontSize="sm"
              px={3}
              _hover={{
                bg: "blue.50",
                transform: "translateX(-4px)",
              }}
              transition="all 0.2s"
            >
              <Box display={{ base: "none", sm: "inline" }}>
                Back to Proposals
              </Box>
            </Button>

            <Breadcrumb
              spacing={2}
              separator={<ChevronRight size={12} color="#9CA3AF" />}
              fontSize="xs"
              color="gray.600"
              display={{ base: "none", md: "inline" }}
            >
              <BreadcrumbItem>
                <BreadcrumbLink
                  onClick={handleBack}
                  display="flex"
                  alignItems="center"
                  gap={1}
                  _hover={{ color: "blue.500" }}
                  transition="color 0.2s"
                >
                  <Home size={12} />
                  <Text>Proposals</Text>
                </BreadcrumbLink>
              </BreadcrumbItem>

              <BreadcrumbItem isCurrentPage>
                <BreadcrumbLink
                  display="flex"
                  alignItems="center"
                  gap={1}
                  color="blue.500"
                  fontWeight="medium"
                >
                  <MdSecurity size={12} />
                  <Text>Terms & Conditions</Text>
                </BreadcrumbLink>
              </BreadcrumbItem>
            </Breadcrumb>
          </VStack>

          <HStack spacing={4}>
            <VStack align="end" spacing={0}>
              <Text fontSize="lg" fontWeight="bold" color="gray.800">
                Terms & Conditions
              </Text>
              <Text fontSize="xs" color="gray.500">
                Manage your proposal terms
              </Text>
            </VStack>
            <Box
              bgGradient="linear(to-br, blue.400, blue.600)"
              p={3}
              borderRadius="xl"
              boxShadow="md"
            >
              <MdSecurity size={20} color="white" />
            </Box>
          </HStack>
        </Flex>
      </Box>

      {/* Search and Filters Header */}
      <Box
        bg="white"
        borderRadius="xl"
        boxShadow="sm"
        border="1px"
        borderColor="gray.200"
        mb="8px"
        p={4}
        mx={{ base: 2, md: 4 }}
      >
        <Flex
          justify="space-between"
          align="center"
          flexDirection={{ base: "column", md: "row" }}
          gap={2}
        >
          {/* Search Input */}
          <Box position="relative" maxW="md" flex={1}>
            <InputGroup>
              <InputLeftElement pointerEvents="none">
                <Search size={16} color="#9CA3AF" />
              </InputLeftElement>
              <Input
                w={{ base: "100%", md: "400px" }}
                type="text"
                placeholder="Search terms name..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                pl="40px"
                pr={4}
                py={2}
                fontSize="xs"
                border="1px"
                borderColor="gray.300"
                borderRadius="lg"
                _focus={{
                  outline: "none",
                  ring: 1,
                  ringColor: "blue.500",
                  borderColor: "blue.500",
                }}
              />
            </InputGroup>
          </Box>

          {/* Action Buttons */}
          <HStack
            spacing={2}
            flexWrap="wrap"
            justify={{ base: "center", md: "flex-end" }}
          >
            {/* New Terms Button */}
            <Button
              size="md"
              leftIcon={<Plus size={16} />}
              onClick={() => navigate("/proposal-terms-editor")}
              fontSize="sm"
              fontWeight="700"
              color="white"
              bgGradient="linear(to-br, green.400, green.600)"
              boxShadow="0 4px 15px -3px rgba(34, 197, 94, 0.4)"
              px={4}
              py={2}
              h="34px"
              borderRadius="xl"
              position="relative"
              overflow="hidden"
              transition="all 0.3s cubic-bezier(0.4, 0, 0.2, 1)"
              _before={{
                content: '""',
                position: "absolute",
                top: 0,
                left: "-100%",
                width: "100%",
                height: "100%",
                bg: "rgba(255, 255, 255, 0.2)",
                transition: "left 0.5s ease",
              }}
              _hover={{
                bgGradient: "linear(to-br, green.500, green.700)",
                boxShadow: "0 8px 25px -5px rgba(34, 197, 94, 0.5)",
                transform: "translateY(-2px)",
                _before: {
                  left: "100%",
                },
              }}
              _active={{
                transform: "translateY(0)",
                boxShadow: "0 4px 12px -3px rgba(34, 197, 94, 0.4)",
              }}
              isDisabled={loading}
            >
              {loading ? "LOading..." : "New Terms"}
            </Button>

            {/* Add Category Button */}
            <Button
              size="md"
              leftIcon={<Plus size={16} />}
              onClick={() => setIscategoryOpen(true)}
              fontSize="sm"
              fontWeight="700"
              color="white"
              bgGradient="linear(to-br, blue.400, blue.600)"
              boxShadow="0 4px 15px -3px rgba(59, 130, 246, 0.4)"
              px={4}
              py={2}
              h="34px"
              borderRadius="xl"
              position="relative"
              overflow="hidden"
              transition="all 0.3s cubic-bezier(0.4, 0, 0.2, 1)"
              _before={{
                content: '""',
                position: "absolute",
                top: 0,
                left: "-100%",
                width: "100%",
                height: "100%",
                bg: "rgba(255, 255, 255, 0.2)",
                transition: "left 0.5s ease",
              }}
              _hover={{
                bgGradient: "linear(to-br, blue.500, blue.700)",
                boxShadow: "0 8px 25px -5px rgba(59, 130, 246, 0.5)",
                transform: "translateY(-2px)",
                _before: {
                  left: "100%",
                },
              }}
              _active={{
                transform: "translateY(0)",
                boxShadow: "0 4px 12px -3px rgba(59, 130, 246, 0.4)",
              }}
              isDisabled={loading}
            >
              {loading ? "Loading..." : "Category"}
            </Button>

            {/* Filters Button */}
            <Button
              size="sm"
              onClick={() => setShowFilters(!showFilters)}
              fontSize="xs"
              fontWeight="medium"
              color="gray.700"
              bg="white"
              border="1px"
              borderColor="gray.300"
              _hover={{ bg: "gray.50" }}
              px={3}
              py={2}
              h="34px"
              borderRadius="lg"
              leftIcon={<Filter size={12} />}
              position="relative"
            >
              Filters
              {getActiveFiltersCount() > 0 && (
                <Badge
                  position="absolute"
                  top="-8px"
                  right="-8px"
                  borderRadius="full"
                  bg="blue.500"
                  color="white"
                  fontSize="10px"
                  minW="20px"
                  h="20px"
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  fontWeight="bold"
                >
                  {getActiveFiltersCount()}
                </Badge>
              )}
            </Button>

            {/* Refresh Button */}
            <Button
              size="sm"
              onClick={handleClearFilters}
              isDisabled={loading}
              fontSize="xs"
              fontWeight="medium"
              color="gray.700"
              bg="white"
              border="1px"
              borderColor="gray.300"
              _hover={!loading ? { bg: "gray.50" } : {}}
              px={3}
              py={2}
              h="34px"
              borderRadius="lg"
              title="Refresh data and clear filters"
              leftIcon={
                <Box
                  as={RefreshCcw}
                  size={14}
                  className={loading ? "refresh-spin" : ""}
                />
              }
            >
              Refresh
            </Button>
          </HStack>
        </Flex>
      </Box>

      {/* Filters Panel */}
      {showFilters && (
        <Box
          p={4}
          bg="white"
          border="1px"
          borderColor="gray.200"
          borderRadius="lg"
          mb="8px"
          boxShadow="sm"
          mx={{ base: 2, md: 4 }}
          position="relative"
        >
          <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={4}>
            {/* Category Filter */}
            <Box>
              <Text fontSize="xs" fontWeight="medium" color="gray.600" mb={2}>
                Category
              </Text>
              <ChakraSelect
                placeholder="Filter by category..."
                isClearable
                options={[
                  { value: "all", label: "All Categories" },
                  ...(filteredTermsCategories?.map((cat: any) => ({
                    value: cat._id,
                    label: cat.name,
                  })) || []),
                ]}
                onChange={(option: any) =>
                  setSelectedCategory(
                    option?.value === "all" ? null : option?.value
                  )
                }
                value={
                  selectedCategory
                    ? {
                        value: selectedCategory,
                        label:
                          filteredTermsCategories?.find(
                            (cat: any) => cat._id === selectedCategory
                          )?.name || "",
                      }
                    : { value: "all", label: "All Categories" }
                }
              />
            </Box>
          </SimpleGrid>
        </Box>
      )}

      {/* Terms List */}
      <Box px={{ base: 2, md: 4 }} pb={8}>
        {loading ? (
          <SimpleGrid columns={{ base: 1, md: 1 }} gap={4}>
            {Array.from({ length: 4 }).map((_, i) => (
              <Skeleton
                key={i}
                height="100px"
                borderRadius="lg"
                startColor="gray.200"
                endColor="gray.300"
              />
            ))}
          </SimpleGrid>
        ) : filteredTerms && filteredTerms.length > 0 ? (
          <Accordion allowMultiple>
            {filteredTerms.map((item: TermsItem) => (
              <AccordionItem
                key={item._id}
                border="1px"
                borderColor="gray.200"
                borderRadius="lg"
                boxShadow="sm"
                mb={3}
                transition="all 0.2s"
                _hover={{
                  boxShadow: "md",
                  borderColor: "blue.200",
                }}
              >
                <h2>
                  <AccordionButton
                    _expanded={{ bg: "blue.50" }}
                    py={4}
                    px={4}
                    transition="all 0.2s"
                  >
                    <Flex
                      flex="1"
                      textAlign="left"
                      align="center"
                      justify="space-between"
                      gap={4}
                      w="full"
                    >
                      {/* Left: Title & Category */}
                      <Flex
                        alignItems={{ base: "flex-start", md: "center" }}
                        gap={3}
                        direction={{ base: "column", md: "row" }}
                        flex={1}
                      >
                        <Box
                          bg="purple.100"
                          p={2}
                          borderRadius="lg"
                          display="flex"
                          alignItems="center"
                          justifyContent="center"
                          flexShrink={0}
                        >
                          <MdSecurity size={18} color="#9333EA" />
                        </Box>
                        <VStack align="start" spacing={0.5} flex={1}>
                          <Text fontSize="sm" fontWeight="700" color="gray.900">
                            {item.title}
                          </Text>
                          <Badge
                            colorScheme="orange"
                            variant="subtle"
                            fontSize="10px"
                            px={2}
                            py={0.5}
                            w="fit-content"
                          >
                            {filteredTermsCategories?.find(
                              (cat: any) => cat._id === item.category
                            )?.name ?? "N/A"}
                          </Badge>
                        </VStack>
                      </Flex>

                      {/* Right: Actions */}
                      <HStack spacing={2} display="flex">
                        {(item.parentId === signinuser?.userId ||
                          item.parentId === signinuser?.parentId) && (
                          <>
                            <IconButton
                              icon={<EditIcon />}
                              aria-label="Edit"
                              size="sm"
                              colorScheme="blue"
                              variant="ghost"
                              onClick={(e: any) => {
                                e.stopPropagation();
                                navigate(
                                  "/proposal-terms-editor?editId=" + item._id,
                                  { state: { template: item } }
                                );
                              }}
                              _hover={{
                                bg: "blue.100",
                              }}
                            />
                            <IconButton
                              icon={<DeleteIcon />}
                              aria-label="Delete"
                              size="sm"
                              colorScheme="red"
                              variant="ghost"
                              onClick={(e: any) => {
                                e.stopPropagation();
                                handleDelete(item._id, item.title);
                              }}
                              _hover={{
                                bg: "red.100",
                              }}
                            />
                          </>
                        )}
                        <AccordionIcon />
                      </HStack>
                    </Flex>
                  </AccordionButton>
                </h2>
                <AccordionPanel pb={4} px={4} pt={4}>
                  <Box
                    bg="gray.50"
                    p={4}
                    borderRadius="lg"
                    border="1px"
                    borderColor="gray.200"
                    fontSize="sm"
                    color="gray.700"
                    lineHeight="1.6"
                    className="editor-content"
                    dangerouslySetInnerHTML={{ __html: item.terms }}
                  />
                </AccordionPanel>
              </AccordionItem>
            ))}
          </Accordion>
        ) : (
          <Box
            textAlign="center"
            py={12}
            bg="gray.50"
            borderRadius="lg"
            border="2px dashed"
            borderColor="gray.200"
          >
            <Box
              bg="purple.100"
              w="60px"
              h="60px"
              borderRadius="full"
              display="flex"
              alignItems="center"
              justifyContent="center"
              mx="auto"
              mb={3}
            >
              <MdSecurity size={28} color="#9333EA" />
            </Box>
            <Text fontWeight="600" color="gray.900" mb={1}>
              No Terms Found
            </Text>
            <Text fontSize="sm" color="gray.600" mb={4}>
              {selectedCategory
                ? "No terms in this category yet."
                : "Create your first terms template to get started."}
            </Text>
            <Button
              colorScheme="purple"
              size="sm"
              onClick={() => navigate("/proposal-terms-editor")}
              leftIcon={<Plus size={14} />}
            >
              Create Terms
            </Button>
          </Box>
        )}
      </Box>

      {/* CSS for refresh button spin */}
      <style>
        {`
          .refresh-spin {
            animation: spin 1s linear infinite;
          }
          @keyframes spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
        `}
      </style>
    </>
  );
};

export default ProposalTerms;
